import React, { ReactNode, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { accountApi } from '../api/account-api';
import { api } from '../api/api';
import { RootState } from './stores/store';
import { ConnectionStatuses, userInfoActions } from './stores/user-info-slice';

export type AccountType = {
    connected?: boolean;
    setAsLoggedIn: VoidFunction;
    setAsConnected: VoidFunction;
    setAsConnecting: VoidFunction;
    setAsConnectionFailed: VoidFunction;
    setAsLoggedOut: () => void;
    setAsForbidden: () => void;

    getStatus: () => string | undefined;

    token?: string;
    status?: ConnectionStatuses;
    userName?: string;
    uniqueId?: string;
    displayName?: string;

    isConnecting: () => boolean;
    isConnectionFailed: () => boolean;
    isConnected: () => boolean;
    isLoggedIn: () => boolean;
    isLoggedOut: () => boolean;
    isForbidden: () => boolean;

    init: () => Promise<any>;
    login: (userName: string, password: string) => Promise<any>;
    register: (
        firstName: string,
        lastName: string,
        userName: string,
        password: string,
        confirmPassword: string
    ) => Promise<any>;
    resetLoginData: (
        status: ConnectionStatuses,
        token: string,
        userName: string,
        uniqueId: string,
        displayName: string
    ) => void;

    logout: () => Promise<any>;
};

export const AccountContext = React.createContext<AccountType>({} as any);

export const useAccount = () => useContext<AccountType>(AccountContext);

export const AccountProvider = ({ children }: { children: ReactNode }) => {
    const dispatch = useDispatch();
    const userInfo = useSelector((state: RootState) => state.userInfo);

    useEffect(() => {
        const onLogoutEvent = (event: any) => {
            if (event.key === 'sync-logout') logout(false);
        };
        window.addEventListener('storage', onLogoutEvent);
        return () => window.removeEventListener('storage', onLogoutEvent);
    }, []);

    function logout(sync: boolean) {
        let result = Promise.resolve();
        if (!api.getToken()) return result;
        account.setAsLoggedOut();
        if (sync) {
            result = accountApi.logout();
            api.setData({});
            localStorage.setItem('sync-logout', Date.now().toString());
        }
        api.setData({});
        return result;
    }

    const account: AccountType = {
        connected: userInfo.status === 'connected' || userInfo.status === 'logged-in',

        token: userInfo.token,
        status: userInfo.status,
        getStatus: () => userInfo.status,

        setAsLoggedIn: () => dispatch(userInfoActions.setStatus('logged-in')),
        setAsConnected: () => dispatch(userInfoActions.setStatus('connected')),
        setAsConnecting: () => dispatch(userInfoActions.setStatus('connecting')),
        setAsConnectionFailed: () => dispatch(userInfoActions.setStatus('connection-failed')),
        setAsLoggedOut: () => dispatch(userInfoActions.setStatus('logged-out')),
        setAsForbidden: () => dispatch(userInfoActions.setStatus('forbidden')),

        userName: userInfo.userName,
        uniqueId: userInfo.uniqueId,
        displayName: userInfo.displayName,

        isConnecting: () => userInfo.status === 'connecting',
        isConnectionFailed: () => userInfo.status === 'connection-failed',
        isConnected: () =>
            userInfo.status === 'connected' ||
            userInfo.status === 'logged-in' ||
            userInfo.status === 'registered',
        isLoggedIn: () => userInfo.status === 'logged-in',
        isLoggedOut: () => userInfo.status === 'logged-out',
        isForbidden: () => userInfo.status === 'forbidden',

        init: () => {
            account.setAsConnecting();
            return accountApi
                .userInfo()
                .then(result => {
                    account.resetLoginData(
                        'connected',
                        result.token,
                        result.userName,
                        result.uniqueId,
                        result.displayName
                    );
                    return result;
                })
                .catch(ex => {
                    if (ex.name === '401') account.setAsLoggedOut();
                    else account.setAsConnectionFailed();
                    throw ex;
                });
        },

        register: (
            firstName: string,
            lastName: string,
            userName: string,
            password: string,
            confirmPassword: string
        ) => {
            return accountApi
                .register({ firstName, lastName, userName, password, confirmPassword })
                .then(result => {
                    account.resetLoginData(
                        'registered',
                        result.token,
                        result.userName,
                        result.uniqueId,
                        result.displayName
                    );

                    return result;
                });
        },

        login: (userName: string, password: string) =>
            accountApi.login({ userName, password }).then(result => {
                account.resetLoginData(
                    'logged-in',
                    result.token,
                    result.userName,
                    result.uniqueId,
                    result.displayName
                );
                return result;
            }),

        resetLoginData: (
            status: ConnectionStatuses,
            token: string,
            userName: string,
            uniqueId: string,
            displayName: string
        ) => {
            dispatch(
                userInfoActions.init({
                    status: status,
                    token,
                    userName,
                    uniqueId,
                    displayName,
                })
            );
            api.setData({ token, expiry: -1, userName });
        },

        logout: () => logout(true),
    };

    useEffect(() => {
        if (userInfo.status === null) return;
        api.onUnauthorized(() => account.setAsLoggedOut());
    }, [api, account]);

    return <AccountContext.Provider value={account}>{children}</AccountContext.Provider>;
};
